/**
 * `preferences` (D188) — the sidebar's top row, and the settings that belong to the person and to
 * the handset rather than to a gate.
 *
 * A **shell** surface, P6's, for exactly the reasons `about` is (`web-ownership-map.ts` carries
 * both): it is the destination of a P6-owned drawer row, it holds no gate, and the appearance half
 * of it is `theme/`'s, which is P6's too. The name half calls one API and that is `patchProfile`.
 *
 * **D247 reverses D188 (j): all four settings sit behind one explicit Save, in the app bar.** The
 * autosave and its *Saved.* line are gone. What D188 (j) got right is kept in full — an empty field
 * never sends, a save compares against the last value the **server accepted** rather than the draft
 * or `session.name`, the value adopted is the one the server **echoed**, and offline disables the
 * name field and says why. What changed is that a draft is now a draft: it lives on this screen
 * until the resident commits it, which is what makes a back press a question worth asking.
 *
 * **The Save button exists only while the screen is dirty**, so an untouched visit carries no
 * control and no prompt; its disappearance is also the success signal, which is what let the
 * *Saved.* line retire rather than be restated somewhere else.
 *
 * **Appearance previews immediately and persists only on Save.** A colour-scheme pill that does not
 * repaint on the tap that picks it is a broken control, so `applyColorScheme` still fires at once —
 * but `writeColorScheme` moved to the save path, and the last **saved** scheme is re-applied on
 * discard and on unmount. The unmount half is not belt-and-braces: the guard cannot see an
 * auth-guard redirect, a session ending, or a hardware back taken while the prompt is open, and
 * every one of those would otherwise leave the app wearing a scheme nobody chose.
 *
 * **The asymmetry between the name and the rest is on purpose and is the reason they share a
 * screen.** The name
 * is account-owned and travels, so it is a `PATCH /users/me` and it is **refused offline** rather
 * than queued (device-cache.md). Appearance is device-owned and does not travel — a phone in dark
 * and a desktop in light is the correct outcome, not a conflict — so it is `localStorage` and it
 * works with no network at all. The disabled field beside the working pills is the clearest
 * statement of that difference the product has. D198's animation and **D266's app-updates mute**
 * are device-local on the same argument, and neither needs a preview — the appearance pill is the
 * only one whose tap has to repaint anything.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ValidationError, normalizeDisplayText } from '@selogate/core'
import { useSession } from '../../auth/context'
import { MAX_NAME_GRAPHEMES } from '../../auth/config'
import { NetworkError } from '../../auth/transport'
import { AppBar, FrameOverlay, HintTip, Icon, UnsavedChangesPopover } from '../../components'
import type { IconName } from '../../components'
import { useTranslation } from '../../i18n/I18nProvider'
import { useLeaveGuard, useNavigation } from '../../router'
import { patchProfile } from '../../screens/shared/api'
import {
  COLOR_SCHEME_OPTIONS,
  applyColorScheme,
  readColorScheme,
  writeColorScheme,
  type ColorSchemePreference,
} from '../../theme/colorScheme'
import {
  OPEN_ANIMATION_OPTIONS,
  readOpenAnimation,
  writeOpenAnimation,
  type OpenAnimationStyle,
} from '../../theme/openAnimation'
import {
  UPDATE_ALERTS_OPTIONS,
  readUpdateAlerts,
  writeUpdateAlerts,
  type UpdateAlerts,
} from '../../pwa/updateAlerts'
import { OpenAnimation } from '../home/OpenAnimation'
import type { GateOpenOutcome } from '../home/GateOpenOverlay'

/**
 * What the row under the field is saying. `idle` renders nothing.
 *
 * D247 removed `saved`: the Save button disappearing when the screen goes clean is the
 * confirmation, and a line that also said so would be the same fact twice.
 */
type SaveState = 'idle' | 'saving' | 'required' | 'invalid' | 'server' | 'network'

/** `as const` so each value stays a literal — `t()` is typed against the catalog's own keys. */
const STATUS_KEYS = {
  saving: 'nameSaving',
  required: 'editNameErrorRequired',
  invalid: 'editNameErrorInvalid',
  server: 'editNameErrorServer',
  network: 'editNameErrorNetwork',
} as const satisfies Record<Exclude<SaveState, 'idle'>, string>

const SCHEME_LABELS = {
  light: 'schemeLight',
  auto: 'schemeAuto',
  dark: 'schemeDark',
} as const satisfies Record<ColorSchemePreference, string>

const SCHEME_ICONS = {
  light: 'sun',
  auto: 'sun-moon',
  dark: 'moon',
} as const satisfies Record<ColorSchemePreference, IconName>

const OPEN_ANIMATION_LABELS = {
  check: 'openAnimationCheck',
  gate: 'openAnimationGate',
} as const satisfies Record<OpenAnimationStyle, string>

/** `check` draws a mark, `gate` is the shipped cloud-at-the-gate leaves — one glyph per overlay.
 * D215's `car` had a third, lucide `gauge`, and **D218 deleted the path rather than leaving it
 * unused**: D181's rule, that an icon named after a control which no longer exists gets
 * re-adopted for a meaning it never had. */
const OPEN_ANIMATION_ICONS = {
  check: 'check',
  gate: 'gate-icon',
} as const satisfies Record<OpenAnimationStyle, IconName>

const UPDATE_ALERTS_LABELS = {
  popover: 'updateAlertsPopover',
  menu: 'updateAlertsMenu',
  mute: 'updateAlertsMute',
} as const satisfies Record<UpdateAlerts, string>

/**
 * A bell, the hamburger, and a struck bell (D285).
 *
 * The bell and the struck bell are D266's pair — the one in the icon set that says *heard* and
 * *not heard* — and the middle option takes **the glyph of the surface it names**, which is the
 * hamburger's own `menu`: the whole of that answer is *mark the menu and nothing else*, and a
 * third bell variant would have to invent a distinction the set does not draw. `Icon` renders the
 * glyph `aria-hidden`, so the label beside it is what carries the meaning either way.
 */
const UPDATE_ALERTS_ICONS = {
  popover: 'bell',
  menu: 'menu',
  mute: 'bell-off',
} as const satisfies Record<UpdateAlerts, IconName>

/**
 * How long the demo waits in `pending` before answering `opened`. The overlay owns the rest of the
 * sequence from there (its own `MIN_WAIT_MS` floor, `ANSWER_HOLD_MS` hold, `FADE_OUT_MS` exit), so
 * this is only "how long does the wait beat run for a resident who isn't waiting on a real gate" —
 * long enough to read as a wait rather than a flash.
 */
const DEMO_ANSWER_DELAY_MS = 1200

export function PreferencesScreen({
  onDeleteAccount,
  deleteAccountDisabled = false,
}: {
  /**
   * Opens P19 §3's account-deletion confirmation, owned by `App` for the same stacking reason
   * the sidebar's version was. **It sits here rather than on Devices (D194)**: this screen is
   * the account — the name it carries and the way it looks — and deleting the account is the
   * last thing that can be done to it. Devices is a list of *sessions*, so the pill was one
   * level of subject away from what the screen was about.
   */
  onDeleteAccount(): void
  /** Offline. Erasure is a cloud write, refused rather than queued. */
  readonly deleteAccountDisabled?: boolean
}): JSX.Element {
  const session = useSession()
  const navigate = useNavigation()
  const { t } = useTranslation('preferences')
  const { t: tAccount } = useTranslation('account')
  const { t: tCommon } = useTranslation('common')

  const name = session.name ?? ''
  const [draft, setDraft] = useState(name)
  const [status, setStatus] = useState<SaveState>('idle')
  const [scheme, setScheme] = useState<ColorSchemePreference>(() => readColorScheme())
  const [animation, setAnimation] = useState<OpenAnimationStyle>(() => readOpenAnimation())
  const [alerts, setAlerts] = useState<UpdateAlerts>(() => readUpdateAlerts())
  /**
   * `null` means no demo is mounted. Non-null is the overlay's own `outcome`, so this doubles as
   * the "a demo is playing" flag the button's `disabled` reads — a second boolean would just be
   * this one restated and could drift from it.
   */
  const [demoOutcome, setDemoOutcome] = useState<GateOpenOutcome | null>(null)

  /**
   * The last value the **server** accepted, which is what a save is compared against — never the
   * rendered `draft`, and never `session.name`, which lags a save by a `rememberProfile` round
   * trip. Without it, a field blurred with nothing typed re-sends the name it already holds, and
   * every visit to this screen costs a `PATCH`.
   */
  const [savedName, setSavedName] = useState(name)
  /**
   * The three device-local counterparts of `savedName` (D247): the last value actually written to
   * storage, which is what their halves of `dirty` compare against and what a discard restores.
   * Seeded from the pills' own initialisers, which already read storage, so the two cannot drift.
   */
  const [savedScheme, setSavedScheme] = useState(scheme)
  const [savedAnimation, setSavedAnimation] = useState(animation)
  const [savedAlerts, setSavedAlerts] = useState(alerts)
  /**
   * **All four are state rather than refs, and that is load-bearing rather than stylistic.**
   * `dirty` is derived from them, so a ref would leave the Save button and the leave guard reading
   * a value no render ever saw: a save whose echo equals what was typed changes no other state, and
   * React would bail out of re-rendering entirely — the button would sit there, permanently, on a
   * screen with nothing left to save. Under D188 (j) the ref was right, because the debounced timer
   * callback had to read it; there is no timer any more.
   */
  const schemeRef = useRef(scheme)
  schemeRef.current = scheme
  const savedSchemeRef = useRef(savedScheme)
  savedSchemeRef.current = savedScheme
  const demoTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  /**
   * `session.request`'s identity changes after **every** API call the app makes — `#set` allocates
   * a new state object and notifies even when no field changed (see `AGENTS.md` §4.9). An effect
   * or a timer that closed over it would re-arm on the very response it produced, so the save
   * reads the function out of a ref instead. Measured cost of getting this wrong elsewhere: 2,965
   * requests from one screen view.
   */
  const sessionRef = useRef(session)
  sessionRef.current = session

  // Same shape, for the demo's own timer: leaving mid-demo must not fire a `setState` after
  // unmount, and clearing it here rather than in `onDone` means a leave-during-`pending` is
  // covered too, not only a leave after the answer.
  useEffect(() => () => clearTimeout(demoTimerRef.current), [])

  /**
   * The name half of a save. Returns whether the name is now committed — `saveAll` needs to know,
   * because the two device-local halves can succeed on a screen where this one cannot even be
   * attempted (offline), and the button must stay put when anything is still outstanding.
   */
  async function saveName(next: string): Promise<boolean> {
    const current = sessionRef.current
    if (!current.online) return false

    let normalized: string
    try {
      // D57 inline, with P0's primitive (§13 "Display-text validation holds the line without over-blocking (D57)") — the same function the
      // server validates with, so a bidi override or a C0 control never leaves the device.
      normalized = normalizeDisplayText(next, MAX_NAME_GRAPHEMES, 'name')
    } catch (caught) {
      if (!(caught instanceof ValidationError)) throw caught
      // **The stored name is left alone.** An empty field is a half-finished edit, not an
      // instruction to erase — and a self-name is never removed server-side (D133 (f)).
      setStatus(next.trim().length === 0 ? 'required' : 'invalid')
      return false
    }
    if (normalized === savedName) {
      setStatus('idle')
      return true
    }

    setStatus('saving')
    try {
      const result = await patchProfile(current.request, { name: normalized })
      if (!result.ok) {
        setStatus(result.status === 422 ? 'invalid' : 'server')
        return false
      }
      await current.rememberProfile(result.data)
      const echoed = result.data.name ?? normalized
      setSavedName(echoed)
      // Adopt the **echoed** value into the field as well as the baseline (D188 (j)), or a server
      // that normalized what was typed would leave the screen permanently dirty against itself.
      setDraft(echoed)
      setStatus('idle')
      return true
    } catch (caught) {
      if (!(caught instanceof NetworkError)) throw caught
      setStatus('network')
      return false
    }
  }

  /**
   * The Save button, and the prompt's save answer.
   *
   * **The three device-local settings commit first and unconditionally.** They cannot fail and they
   * do not need the network, so an offline resident who changed their appearance still gets it
   * saved; only the name is refused, and the status line already says why. Returning `false` in
   * that case keeps the button on screen, which is the honest report — something is still unsaved.
   */
  async function saveAll(): Promise<boolean> {
    if (scheme !== savedScheme) {
      writeColorScheme(scheme)
      setSavedScheme(scheme)
    }
    if (animation !== savedAnimation) {
      writeOpenAnimation(animation)
      setSavedAnimation(animation)
    }
    if (alerts !== savedAlerts) {
      writeUpdateAlerts(alerts)
      setSavedAlerts(alerts)
    }
    if (draft === savedName) return true
    return await saveName(draft)
  }

  /** The prompt's discard answer: every field back to what is stored, appearance included. */
  function discard(): void {
    setDraft(savedName)
    setScheme(savedScheme)
    setAnimation(savedAnimation)
    setAlerts(savedAlerts)
    applyColorScheme(savedScheme)
    setStatus('idle')
  }

  function chooseScheme(next: ColorSchemePreference): void {
    // **Applied but not stored (D247).** The repaint is a preview: a scheme pill that did not
    // change the app on the tap that picked it would be a control with no visible effect, which is
    // worse than the confirm step this screen gained. `writeColorScheme` runs in `saveAll`, and
    // `discard` — plus the unmount effect above — put the previous scheme back.
    setScheme(next)
    applyColorScheme(next)
  }

  /**
   * **No preview and no revert-on-unmount, and copying the appearance pill's would be wrong.**
   *
   * `applyColorScheme` fires on the tap because *a control that does not repaint on the tap that
   * picks it is a broken control* — and the appearance pill repaints the whole screen. This pill's
   * own thumb moves, which **is** the repaint; everything else this setting touches is elsewhere —
   * a dot on an app bar that is not this control, and since D285 a prompt on a screen the resident
   * is not on and will next see at a launch this one cannot reach anyway. So the value applies on
   * Save, and the unmount-revert effect the scheme needs has nothing here to undo.
   */
  function chooseAlerts(next: UpdateAlerts): void {
    setAlerts(next)
  }

  function chooseAnimation(next: OpenAnimationStyle): void {
    // Nothing to apply: the animation is read at the moment an overlay mounts, so the Demo button
    // beside it previews the draft simply by rendering `animation`. Stored by `saveAll`.
    setAnimation(next)
  }

  /**
   * Mounts `<OpenAnimation>` at `pending` and answers `opened` after `DEMO_ANSWER_DELAY_MS`, so the
   * whole wait→answer→exit sequence plays exactly as it would after a real open. `onDone` (the
   * overlay's own exit-complete callback) is what unmounts it — not this timer, which only carries
   * the outcome from `pending` to `opened`.
   */
  function playDemo(): void {
    setDemoOutcome('pending')
    demoTimerRef.current = setTimeout(() => setDemoOutcome('opened'), DEMO_ANSWER_DELAY_MS)
  }

  /**
   * Put the saved scheme back on the way out.
   *
   * This is the catch-all for every exit the leave guard cannot see: the auth-guard redirect, a
   * session ending, the delete-account flow, and a hardware back taken while the prompt is open
   * (which owns no history entry by design). Without it the app keeps rendering a scheme the
   * resident previewed and never saved.
   *
   * **Guarded on the comparison, which is what makes it StrictMode-safe.** React preserves refs and
   * state across the simulated unmount/remount, and at mount time `scheme === savedScheme`, so the
   * double-invoke is a no-op — where an unguarded revert would undo a preview the remount cannot
   * re-apply, `useState` initialisers not running a second time.
   */
  useEffect(
    () => () => {
      if (schemeRef.current !== savedSchemeRef.current) applyColorScheme(savedSchemeRef.current)
    },
    [],
  )

  const offline = !session.online

  const dirty =
    draft !== savedName || scheme !== savedScheme || animation !== savedAnimation || alerts !== savedAlerts

  const guard = useLeaveGuard(
    'preferences:leave',
    dirty,
    useCallback(() => navigate.back(), [navigate]),
  )

  /** Save from the prompt: leave only once everything has actually committed. */
  async function saveAndLeave(): Promise<void> {
    if (await saveAll()) guard.leave()
    else guard.stay()
  }

  return (
    <>
      <AppBar
        title={t('title')}
        onBack={guard.requestLeave}
        action={
          dirty
            ? {
                label: status === 'saving' ? tCommon('saving') : tCommon('save'),
                onClick: () => void saveAll(),
                disabled: status === 'saving',
              }
            : undefined
        }
      />

      {/*
        No section title over the name field (D195). *Your name* named one field, sitting directly
        above a `<label>` that says **שם** — the same word twice, one line apart — where
        `appearance` below genuinely titles a control that has no label of its own. The screen's
        two settings are told apart by their own labels and by the divider the pill sits under.
      */}
      {/*
        `.has-tip` on the row rather than on the label: the bubble is positioned against whichever
        ancestor owns the 16px content inset, and `top: 100%` then drops it under the input — which
        is exactly where the sentence it replaced used to sit. See `global.css`'s `.has-tip` block.
      */}
      <div className="form-row has-tip">
        <label htmlFor="selfName">
          {t('name')}
          <HintTip text={t('nameHint')} label={t('nameHintLabel')} />
        </label>
        {/*
          `dir="auto"` rather than the `Bidi` component or a fixed direction: a self-name may be
          Hebrew, Latin, or mixed (PEC-3), and on an *input* the direction has to follow what is
          being typed rather than what was typed first.
        */}
        <input
          id="selfName"
          className="input"
          value={draft}
          dir="auto"
          maxLength={MAX_NAME_GRAPHEMES}
          autoComplete="name"
          spellCheck={false}
          disabled={offline}
          onChange={(e) => {
            setDraft(e.target.value)
            setStatus('idle')
          }}
          onKeyDown={(e) => {
            // Enter saves, as the primary action of a one-field form. It was a blur-and-flush under
            // the autosave; there is no debounce left to flush, so it commits directly.
            if (e.key === 'Enter') {
              e.currentTarget.blur()
              if (dirty) void saveAll()
            }
          }}
        />
      </div>
      {/*
        The save line. It **reports** now and nothing else — the standing explanation moved into the
        header's `(i)`, so an idle field renders no line at all rather than an empty band the other
        two settings do not have. Offline outranks every save state: the reason nothing is
        happening is that nothing *can* happen, and that is a report rather than an explanation.
      */}
      {offline || status !== 'idle' ? (
        <p className={`hint status${status === 'saving' || offline ? '' : ' error'}`}>
          {offline ? t('nameOffline') : t(STATUS_KEYS[status as Exclude<SaveState, 'idle'>])}
        </p>
      ) : null}

      <div className="section-title setting-title has-tip">
        {t('appearance')}
        <HintTip text={t('appearanceHint')} label={t('appearanceHintLabel')} />
      </div>
      <div className="scheme-row">
        <div className="scheme-segmented" role="group" aria-label={t('appearance')}>
          {COLOR_SCHEME_OPTIONS.map((option) => (
            <button
              key={option}
              className={`scheme-segment${option === scheme ? ' selected' : ''}`}
              type="button"
              /*
               * `aria-pressed` rather than `aria-checked`: this is a group of toggle buttons, not
               * a radiogroup, and a radiogroup would owe arrow-key roving focus that three
               * adjacent buttons in a `<div role="group">` do not.
               */
              aria-pressed={option === scheme}
              onClick={() => chooseScheme(option)}
            >
              <Icon name={SCHEME_ICONS[option]} size={16} />
              <span>{t(SCHEME_LABELS[option])}</span>
            </button>
          ))}
        </div>
      </div>

      {/*
        The opening-animation pill — same segmented control as `appearance` above it, one field
        over, and the same autosave posture (D188 (j)): no confirm button, stored and applied on
        the tap that picks it. Device-local for the same reason `scheme` is (`openAnimation.ts`),
        which is why it works offline exactly as the appearance pill does.
      */}
      <div className="section-title setting-title has-tip">
        {t('openAnimation')}
        <HintTip text={t('openAnimationHint')} label={t('openAnimationHintLabel')} />
      </div>
      <div className="scheme-row">
        <div className="scheme-segmented" role="group" aria-label={t('openAnimation')}>
          {OPEN_ANIMATION_OPTIONS.map((option) => (
            <button
              key={option}
              className={`scheme-segment${option === animation ? ' selected' : ''}`}
              type="button"
              // `aria-pressed`, not `aria-checked` — same reasoning as the appearance pill: a
              // group of toggle buttons, not a radiogroup owing roving focus.
              aria-pressed={option === animation}
              onClick={() => chooseAnimation(option)}
            >
              <Icon name={OPEN_ANIMATION_ICONS[option]} size={16} />
              <span>{t(OPEN_ANIMATION_LABELS[option])}</span>
            </button>
          ))}
        </div>
      </div>
      {/*
        `.btn-secondary`, not `.deletion-pill.neutral`: the deletion pill is `width: fit-content`
        and its class name carries account-erasure framing even in its grey `.neutral` form, neither
        of which fits a demo control. `.btn-secondary` is the plain full-width neutral action this
        app already reuses elsewhere (People's copy-from/copy-to) with no destructive connotation.
      */}
      <button
        className="btn-secondary btn-with-icon demo-action"
        type="button"
        onClick={playDemo}
        disabled={demoOutcome !== null}
      >
        <Icon name="play" size={16} />
        <span>{t('openAnimationDemo')}</span>
      </button>
      {/*
        D266's app-updates pill — the fourth setting, and the second device-local one that needs no
        preview.

        **Same segmented control as the two pills above it**, reusing `.scheme-segmented` /
        `.scheme-segment` rather than cutting a switch: this app has no toggle component and this
        is not the place to mint one, which is D245's own precedent read from the other direction.
        D188 (k)'s rule comes with the control — a filled `--accent`/`--inverse` thumb in a `--g1`
        track, never an outline — so it needs **no scheme block and no new CSS at all**.

        **It is a mute, not an off**, and the hint carries the half a resident cannot discover by
        looking: the Update row stays in the sidebar. *Notifications* is refused as a word — push
        is Stage 2 (D173/P21) and `POST_NOTIFICATIONS` is on `FORBIDDEN_ANDROID_PERMISSIONS`, so a
        setting wearing it would promise a mechanism the product has deliberately not built.

        **D285 made it three, and the control did not change.** The appearance pill above already
        lays out three segments, so the widening costs no CSS and no measurement — which is the
        reason the launch prompt became a third *answer* here rather than a second setting beside
        this one: the question a resident is asking is *how do you want to be told*, and it has one
        answer.
      */}
      <div className="section-title setting-title has-tip">
        {t('updateAlerts')}
        <HintTip text={t('updateAlertsHint')} label={t('updateAlertsHintLabel')} />
      </div>
      <div className="scheme-row">
        <div className="scheme-segmented" role="group" aria-label={t('updateAlerts')}>
          {UPDATE_ALERTS_OPTIONS.map((option) => (
            <button
              key={option}
              className={`scheme-segment${option === alerts ? ' selected' : ''}`}
              type="button"
              // `aria-pressed`, not `aria-checked` — same reasoning as the two pills above: a group
              // of toggle buttons, not a radiogroup owing roving focus.
              aria-pressed={option === alerts}
              onClick={() => chooseAlerts(option)}
            >
              <Icon name={UPDATE_ALERTS_ICONS[option]} size={16} />
              <span>{t(UPDATE_ALERTS_LABELS[option])}</span>
            </button>
          ))}
        </div>
      </div>
      {/*
        **Through `FrameOverlay`, for the reason Home mounts it that way** — and it is not a
        tidiness point: `.app-screen` is `position: relative` *and* the scroll container, so an
        `inset: 0` overlay rendered here anchors to the scrolled content box. Measured in the
        browser: the demo painted below the app bar and moved with the scroll, which makes it a
        preview of something the product never does. The portal buys the shell anchoring without
        hoisting the demo's state out of this screen.

        One at a time is still the rule — the button above is disabled while `demoOutcome` is
        non-null — because two overlays would be two of every SVG filter id in one document.
      */}
      {demoOutcome === null ? null : (
        <FrameOverlay>
          <OpenAnimation style={animation} outcome={demoOutcome} onDone={() => setDemoOutcome(null)} />
        </FrameOverlay>
      )}

      {/*
        P19 §3's account-deletion control (D194), below both settings because it is what ends the
        thing they configure. Same pill as before — grey rather than red: this screen is not
        itself a destructive one, and the tap opens a confirmation rather than deleting outright.
        It is disabled offline for the same reason the name field above it is, and by the same
        `session.online` the caller reads: erasure is a cloud write, refused rather than queued.
      */}
      <button
        className="deletion-pill neutral"
        type="button"
        onClick={onDeleteAccount}
        disabled={deleteAccountDisabled}
      >
        {tAccount('deleteRow')}
      </button>

      {/*
        D247's leave prompt. Rendered last so it is the final sibling in the tree, and outside
        `FrameOverlay` deliberately — it is a `.popover`, whose own backdrop already covers the
        frame, and the three siblings it joins are rendered the same way by their callers.
      */}
      {guard.prompting ? (
        <UnsavedChangesPopover
          title={tCommon('unsavedTitle')}
          body={tCommon('unsavedBody')}
          saveLabel={tCommon('save')}
          discardLabel={tCommon('discardChanges')}
          stayLabel={tCommon('stayHere')}
          saveDisabled={status === 'saving'}
          error={offline && draft !== savedName ? t('nameOffline') : undefined}
          onSave={() => void saveAndLeave()}
          onDiscard={() => {
            discard()
            guard.leave()
          }}
          onStay={guard.stay}
        />
      ) : null}
    </>
  )
}
